import { useEffect, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";

export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  size = "md",
  showClose = true,
}: {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  size?: "sm" | "md" | "lg" | "xl";
  showClose?: boolean;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open) return null;

  const sizeClass = {
    sm: "max-w-sm",
    md: "max-w-lg",
    lg: "max-w-2xl",
    xl: "max-w-4xl",
  }[size];

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 animate-fade-in"
      role="dialog"
      aria-modal="true"
    >
      {/* Backdrop click closes the modal. The original handler lived on the
          outer wrapper and used `e.target === e.currentTarget`, but the
          absolute-positioned backdrop sits on top of the wrapper so the
          click target never matches — outside-click was effectively a
          no-op. Putting the handler directly on the backdrop fixes that
          for every dialog in the app at once. */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onMouseDown={onClose}
      />
      <div
        className={cn(
          // Cap height to the viewport (minus padding) and make the body
          // scroll, so tall dialogs never overflow past the screen.
          "relative flex max-h-[calc(100dvh-2rem)] w-full flex-col rounded-xl border border-zinc-800 bg-zinc-950 shadow-2xl shadow-shade/50 animate-slide-up",
          sizeClass
        )}
        // Stop click bubbling so clicks INSIDE the modal don't accidentally
        // hit the backdrop and close it.
        onMouseDown={(e) => e.stopPropagation()}
      >
        {(title || showClose) && (
          <div className="flex items-start justify-between gap-4 border-b border-zinc-800 px-5 py-4">
            <div className="min-w-0">
              {title && <h2 className="text-base font-semibold text-zinc-100 truncate">{title}</h2>}
              {description && <p className="mt-1 text-xs text-zinc-500">{description}</p>}
            </div>
            {showClose && (
              <button
                type="button"
                onClick={onClose}
                className="-mr-1 -mt-1 inline-flex h-8 w-8 items-center justify-center rounded-md text-zinc-500 hover:bg-zinc-800/60 hover:text-zinc-200"
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        )}
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>
      </div>
    </div>,
    document.body
  );
}

export function ModalFooter({ children }: { children: ReactNode }) {
  // Sticky so the action buttons stay visible while the body scrolls above them.
  return <div className="sticky bottom-0 z-10 -mx-5 -mb-4 mt-5 flex items-center justify-end gap-2 border-t border-zinc-800 bg-zinc-950 px-5 py-3">{children}</div>;
}

/* ─── Drawer (right-side, full-height) ─────────────────────────────────────── */
export function Drawer({
  open,
  onClose,
  title,
  children,
  width = "md",
}: {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  children: ReactNode;
  width?: "sm" | "md" | "lg" | "xl";
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);
  if (!open) return null;
  const widthClass = {
    sm: "max-w-sm",
    md: "max-w-md",
    lg: "max-w-xl",
    xl: "max-w-3xl",
  }[width];
  return createPortal(
    <div className="fixed inset-0 z-50 flex animate-fade-in" role="dialog" aria-modal="true">
      <div className="flex-1 bg-black/60 backdrop-blur-sm" onMouseDown={onClose} />
      <div className={cn("flex h-full w-full flex-col border-l border-zinc-800 bg-zinc-950 shadow-2xl shadow-shade/50 animate-slide-in-right", widthClass)}>
        {title !== undefined && (
          <div className="flex items-center justify-between gap-4 border-b border-zinc-800 px-5 py-4">
            <h2 className="text-base font-semibold text-zinc-100 truncate">{title}</h2>
            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md text-zinc-500 hover:bg-zinc-800/60 hover:text-zinc-200"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}
        <div className="flex-1 overflow-y-auto">{children}</div>
      </div>
    </div>,
    document.body
  );
}
